<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import PreChatFields from './PreChatFields.vue';
import { getPreChatFields, standardFieldKeys } from 'dashboard/helper/preChat';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    PreChatFields,
    WootMessageEditor,
    NextButton,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      preChatFormEnabled: false,
      preChatMessage: '',
      preChatFields: [],
      offlineFormEnabled: false,
      offlineFormMessage: '',
      offlineFormSuccessMessage: '',
      offlineFormSubmitAction: 'open_conversation',
      gdprConsentEnabled: false,
      gdprConsentMessage: '',
      gdprPolicyUrl: '',
      gdprPolicyLinkLabel: '',
      gdprCheckboxLabel: '',
      gdprPolicyVersion: 'v1',
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
      customAttributes: 'attributes/getAttributes',
    }),
    preChatFieldOptions() {
      const { pre_chat_form_options: preChatFormOptions } = this.inbox;
      return getPreChatFields({
        preChatFormOptions,
        customAttributes: this.customAttributes,
      });
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      const { pre_chat_form_enabled: preChatFormEnabled } = this.inbox;
      this.preChatFormEnabled = preChatFormEnabled;
      const {
        offline_form_enabled: offlineFormEnabled = false,
        offline_form_message: offlineFormMessage = '',
        offline_form_success_message: offlineFormSuccessMessage = '',
        offline_form_submit_action: offlineFormSubmitAction = 'open_conversation',
        gdpr_consent_enabled: gdprConsentEnabled = false,
        gdpr_consent_message: gdprConsentMessage = '',
        gdpr_policy_url: gdprPolicyUrl = '',
        gdpr_policy_link_label: gdprPolicyLinkLabel = '',
        gdpr_checkbox_label: gdprCheckboxLabel = '',
        gdpr_policy_version: gdprPolicyVersion = 'v1',
      } = this.inbox.pre_chat_form_options || {};
      const {
        pre_chat_message: preChatMessage,
        pre_chat_fields: preChatFields,
      } = this.preChatFieldOptions || {};
      this.preChatMessage = preChatMessage;
      this.preChatFields = preChatFields;
      this.offlineFormEnabled = offlineFormEnabled;
      this.offlineFormMessage = offlineFormMessage;
      this.offlineFormSuccessMessage = offlineFormSuccessMessage;
      this.offlineFormSubmitAction = offlineFormSubmitAction;
      this.gdprConsentEnabled = gdprConsentEnabled;
      this.gdprConsentMessage = gdprConsentMessage;
      this.gdprPolicyUrl = gdprPolicyUrl;
      this.gdprPolicyLinkLabel = gdprPolicyLinkLabel;
      this.gdprCheckboxLabel = gdprCheckboxLabel;
      this.gdprPolicyVersion = gdprPolicyVersion;
    },
    isFieldEditable(item) {
      return !!standardFieldKeys[item.name] || !item.enabled;
    },
    handlePreChatFieldOptions(event, type, item) {
      this.preChatFields.forEach((field, index) => {
        if (field.name === item.name) {
          this.preChatFields[index][type] = !item[type];
        }
      });
    },

    changePreChatFieldFieldsOrder(updatedPreChatFieldOptions) {
      this.preChatFields = updatedPreChatFieldOptions;
    },

    async updateInbox() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            pre_chat_form_enabled: this.preChatFormEnabled,
            pre_chat_form_options: {
              pre_chat_message: this.preChatMessage,
              pre_chat_fields: this.preChatFields,
              offline_form_enabled: this.offlineFormEnabled,
              offline_form_message: this.offlineFormMessage,
              offline_form_success_message: this.offlineFormSuccessMessage,
              offline_form_submit_action: this.offlineFormSubmitAction,
              gdpr_consent_enabled: this.gdprConsentEnabled,
              gdpr_consent_message: this.gdprConsentMessage,
              gdpr_policy_url: this.gdprPolicyUrl,
              gdpr_policy_link_label: this.gdprPolicyLinkLabel,
              gdpr_checkbox_label: this.gdprCheckboxLabel,
              gdpr_policy_version: this.gdprPolicyVersion,
            },
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      }
    },
  },
};
</script>

<template>
  <div class="mx-8 my-2 text-base">
    <div class="mx-0 mt-6 mb-3">
      {{ $t('INBOX_MGMT.PRE_CHAT_FORM.DESCRIPTION') }}
    </div>
    <form class="flex flex-col" @submit.prevent="updateInbox">
      <label class="w-1/4">
        {{ $t('INBOX_MGMT.PRE_CHAT_FORM.ENABLE.LABEL') }}
        <select v-model="preChatFormEnabled">
          <option :value="true">
            {{ $t('INBOX_MGMT.PRE_CHAT_FORM.ENABLE.OPTIONS.ENABLED') }}
          </option>
          <option :value="false">
            {{ $t('INBOX_MGMT.PRE_CHAT_FORM.ENABLE.OPTIONS.DISABLED') }}
          </option>
        </select>
      </label>
      <label class="w-1/4 mt-4">
        {{ $t('INBOX_MGMT.PRE_CHAT_FORM.OFFLINE_FORM.ENABLE.LABEL') }}
        <select v-model="offlineFormEnabled">
          <option :value="true">
            {{
              $t('INBOX_MGMT.PRE_CHAT_FORM.OFFLINE_FORM.ENABLE.OPTIONS.ENABLED')
            }}
          </option>
          <option :value="false">
            {{
              $t(
                'INBOX_MGMT.PRE_CHAT_FORM.OFFLINE_FORM.ENABLE.OPTIONS.DISABLED'
              )
            }}
          </option>
        </select>
      </label>
      <div v-if="preChatFormEnabled">
        <div>
          <label>
            {{ $t('INBOX_MGMT.PRE_CHAT_FORM.PRE_CHAT_MESSAGE.LABEL') }}
          </label>
          <WootMessageEditor
            v-model="preChatMessage"
            class="message-editor"
            :placeholder="
              $t('INBOX_MGMT.PRE_CHAT_FORM.PRE_CHAT_MESSAGE.PLACEHOLDER')
            "
          />
        </div>
      </div>
      <div v-if="offlineFormEnabled" class="mt-4">
        <div>
          <label>
            {{ $t('INBOX_MGMT.PRE_CHAT_FORM.OFFLINE_FORM.MESSAGE.LABEL') }}
          </label>
          <WootMessageEditor
            v-model="offlineFormMessage"
            class="message-editor"
            :placeholder="
              $t('INBOX_MGMT.PRE_CHAT_FORM.OFFLINE_FORM.MESSAGE.PLACEHOLDER')
            "
          />
        </div>
        <div class="mt-4">
          <label>
            {{
              $t('INBOX_MGMT.PRE_CHAT_FORM.OFFLINE_FORM.SUCCESS_MESSAGE.LABEL')
            }}
          </label>
          <WootMessageEditor
            v-model="offlineFormSuccessMessage"
            class="message-editor"
            :placeholder="
              $t(
                'INBOX_MGMT.PRE_CHAT_FORM.OFFLINE_FORM.SUCCESS_MESSAGE.PLACEHOLDER'
              )
            "
          />
        </div>
        <label class="w-1/4 mt-4">
          {{ $t('INBOX_MGMT.PRE_CHAT_FORM.OFFLINE_FORM.SUBMIT_ACTION.LABEL') }}
          <select v-model="offlineFormSubmitAction">
            <option value="open_conversation">
              {{
                $t(
                  'INBOX_MGMT.PRE_CHAT_FORM.OFFLINE_FORM.SUBMIT_ACTION.OPTIONS.OPEN_CONVERSATION'
                )
              }}
            </option>
            <option value="show_success_message">
              {{
                $t(
                  'INBOX_MGMT.PRE_CHAT_FORM.OFFLINE_FORM.SUBMIT_ACTION.OPTIONS.SHOW_SUCCESS_MESSAGE'
                )
              }}
            </option>
          </select>
        </label>
      </div>
      <div v-if="preChatFormEnabled || offlineFormEnabled" class="mt-4">
        <label class="w-1/4 mt-4">
          {{ $t('INBOX_MGMT.PRE_CHAT_FORM.GDPR.ENABLE.LABEL') }}
          <select v-model="gdprConsentEnabled">
            <option :value="true">
              {{ $t('INBOX_MGMT.PRE_CHAT_FORM.GDPR.ENABLE.OPTIONS.ENABLED') }}
            </option>
            <option :value="false">
              {{ $t('INBOX_MGMT.PRE_CHAT_FORM.GDPR.ENABLE.OPTIONS.DISABLED') }}
            </option>
          </select>
        </label>
        <template v-if="gdprConsentEnabled">
          <div class="mt-4">
            <label>
              {{ $t('INBOX_MGMT.PRE_CHAT_FORM.GDPR.MESSAGE.LABEL') }}
            </label>
            <WootMessageEditor
              v-model="gdprConsentMessage"
              class="message-editor"
              :placeholder="
                $t('INBOX_MGMT.PRE_CHAT_FORM.GDPR.MESSAGE.PLACEHOLDER')
              "
            />
          </div>
          <label class="mt-4">
            {{ $t('INBOX_MGMT.PRE_CHAT_FORM.GDPR.CHECKBOX_LABEL.LABEL') }}
            <input
              v-model="gdprCheckboxLabel"
              type="text"
              :placeholder="
                $t('INBOX_MGMT.PRE_CHAT_FORM.GDPR.CHECKBOX_LABEL.PLACEHOLDER')
              "
            />
          </label>
          <label class="mt-4">
            {{ $t('INBOX_MGMT.PRE_CHAT_FORM.GDPR.POLICY_URL.LABEL') }}
            <input
              v-model="gdprPolicyUrl"
              type="url"
              :placeholder="
                $t('INBOX_MGMT.PRE_CHAT_FORM.GDPR.POLICY_URL.PLACEHOLDER')
              "
            />
          </label>
          <label class="mt-4">
            {{ $t('INBOX_MGMT.PRE_CHAT_FORM.GDPR.POLICY_LINK_LABEL.LABEL') }}
            <input
              v-model="gdprPolicyLinkLabel"
              type="text"
              :placeholder="
                $t(
                  'INBOX_MGMT.PRE_CHAT_FORM.GDPR.POLICY_LINK_LABEL.PLACEHOLDER'
                )
              "
            />
          </label>
          <label class="mt-4">
            {{ $t('INBOX_MGMT.PRE_CHAT_FORM.GDPR.POLICY_VERSION.LABEL') }}
            <input
              v-model="gdprPolicyVersion"
              type="text"
              :placeholder="
                $t('INBOX_MGMT.PRE_CHAT_FORM.GDPR.POLICY_VERSION.PLACEHOLDER')
              "
            />
          </label>
        </template>
      </div>
      <div v-if="preChatFormEnabled || offlineFormEnabled" class="mt-4">
        <div>
          <label>{{ $t('INBOX_MGMT.PRE_CHAT_FORM.SET_FIELDS') }}</label>
          <table class="table w-full table-striped mt-4">
            <thead class="thead-dark">
              <tr
                class="[&>th]:font-semibold [&>th]:tracking-[1px] ltr:[&>th]:text-left rtl:[&>th]:text-right [&>th]:px-2.5 [&>th]:uppercase [&>th]:text-n-slate-12"
              >
                <th scope="col" />
                <th scope="col" />
                <th scope="col">
                  {{ $t('INBOX_MGMT.PRE_CHAT_FORM.SET_FIELDS_HEADER.KEY') }}
                </th>
                <th scope="col">
                  {{ $t('INBOX_MGMT.PRE_CHAT_FORM.SET_FIELDS_HEADER.TYPE') }}
                </th>
                <th scope="col">
                  {{
                    $t('INBOX_MGMT.PRE_CHAT_FORM.SET_FIELDS_HEADER.REQUIRED')
                  }}
                </th>
                <th scope="col">
                  {{ $t('INBOX_MGMT.PRE_CHAT_FORM.SET_FIELDS_HEADER.LABEL') }}
                </th>
                <th scope="col">
                  {{
                    $t(
                      'INBOX_MGMT.PRE_CHAT_FORM.SET_FIELDS_HEADER.PLACE_HOLDER'
                    )
                  }}
                </th>
              </tr>
            </thead>
            <PreChatFields
              :pre-chat-fields="preChatFields"
              @update="handlePreChatFieldOptions"
              @drag-end="changePreChatFieldFieldsOrder"
            />
          </table>
        </div>
      </div>
      <div class="w-auto my-4">
        <NextButton
          type="submit"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE_PRE_CHAT_FORM_SETTINGS')"
          :is-loading="uiFlags.isUpdating"
        />
      </div>
    </form>
  </div>
</template>

<style scoped lang="scss">
.message-editor {
  @apply px-3;

  ::v-deep {
    .ProseMirror-menubar {
      @apply rounded-tl-[4px];
    }
  }
}
</style>
